import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import Divider from 'components/Divider';
import useSiteCopySelector from 'components/useSiteCopySelector';
import {
  makeSelectClientDetails,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import { useFirestore } from 'react-redux-firebase';
import Share from './Share';
import { sanitizeLink } from '../../../utils/stringUtils';

const useStyles = makeStyles(theme => ({
  root: {
    padding: ({ invisiblePdfContent, visiblePdfClinicalContent }) =>
      invisiblePdfContent || visiblePdfClinicalContent
        ? '10px 5%'
        : '25px 5% 0px',
    backgroundColor: '#FFFFFF',
  },
  image: {
    width: ({ height }) => (height === 0 ? 0 : height - 16),
    height: ({ height }) => (height === 0 ? 0 : height - 16),
    minWidth: 72,
    minHeight: 72,
    maxWidth: 94,
    maxHeight: 94,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 80,
      maxHeight: 80,
    },
    objectFit: 'cover',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
  },
  title: {
    [theme.breakpoints.down('xs')]: {
      ...theme.typography.h5,
    },
    direction: ({ textDirection }) => textDirection,
  },
  divider: {
    paddingTop: 25,
    backgroundColor: '#FFFFFF',
  },
  pdfDiv: {
    padding: '10px 0 10px 5%',
    textAlign: 'end',
  },
  buttonWrapper: {
    width: 175,
    border: '1px solid #E3E3E3',
    borderRadius: 5,
    height: 53,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      border: 'none',
      width: 'auto',
      '& p': {
        display: 'none',
      },
      position: 'absolute',
      top: 8,
      right: 8,
    },
  },
  buttonRoot: {
    color: theme.palette.text.primary,
    padding: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

function Header({
  className,
  id,
  image,
  isFullWidth,
  title,
  invisiblePdfContent,
  answers,
  accessCodeProps,
  visiblePdfClinicalContent,
  showShareButton = true,
  actionsNode,
}) {
  const reportId = _get(answers, 'report');
  const [accessCode, setAccessCode] = useState(_get(answers, 'accessCode'));
  const [height, setHeight] = useState(0);
  const textDirection = useSelector(makeSelectTextDirection());
  const targetRef = useRef();
  const classes = useStyles({
    height,
    invisiblePdfContent,
    visiblePdfClinicalContent,
    textDirection,
  });
  const [assessmentResultsConfig] = useSiteCopySelector(['assessment-results']);
  const clientDetails = useSelector(makeSelectClientDetails());
  const firestore = useFirestore();
  // const { slug } = useParams();

  useEffect(() => {
    if (!accessCode && reportId) {
      const unsubscribe = firestore
        .collection('assessment_reports')
        .doc(reportId)
        .onSnapshot(snapshot => {
          const data = snapshot.data();
          if (data.accessCode) {
            setAccessCode(data.accessCode);
            unsubscribe();
          }
        });
    }
  }, [accessCode, reportId, firestore]);

  useLayoutEffect(() => {
    if (targetRef.current) {
      setHeight(targetRef.current.offsetHeight);
    }
  }, []);

  const header = _get(assessmentResultsConfig, 'pageHeader');

  const shouldShowShareButton = !_get(
    clientDetails,
    'metadata.hideLeftPanelActions',
    false,
  );
  const now = new Date();
  const finalTakenOnDate = new Date(
    now.valueOf() + now.getTimezoneOffset() * 60 * 1000,
  );

  const visibleShare =
    isFullWidth &&
    shouldShowShareButton &&
    !invisiblePdfContent &&
    !visiblePdfClinicalContent;

  return (
    <>
      <Grid item xs={12} className={_classNames(classes.root, className)}>
        <Grid container spacing={2}>
          {image && (
            <Grid item>
              <img src={image} className={classes.image} alt={title} />
            </Grid>
          )}
          <Grid item xs className={classes.header} ref={targetRef}>
            <Typography variant="h6" color="textPrimary">
              {header}
            </Typography>
            <Typography
              variant={
                invisiblePdfContent
                  ? 'h6'
                  : visiblePdfClinicalContent
                  ? 'h5'
                  : 'h4'
              }
              align="left"
              color="textPrimary"
              className={classes.title}
            >
              {`“${title}”`}
            </Typography>
            {visibleShare && (
              <Hidden mdUp>
                {showShareButton && <Share title={title} />}
                {actionsNode}
              </Hidden>
            )}
          </Grid>
          {visibleShare && (
            <Hidden smDown>
              <Grid item className={classes.header}>
                {showShareButton && <Share title={title} id={id} />}
                {actionsNode}
              </Grid>
            </Hidden>
          )}
          {(invisiblePdfContent || visiblePdfClinicalContent) && (
            <Grid className={classes.pdfDiv}>
              {(accessCode || accessCodeProps) && (
                <Grid item className={classes.header}>
                  <Typography variant="overline">
                    Access Code: <b>{accessCode || accessCodeProps}</b>
                  </Typography>
                </Grid>
              )}

              <Grid item className={classes.header}>
                <Typography variant="overline">{`Taken on ${format(
                  finalTakenOnDate,
                  'MMM d, yyyy',
                )}`}</Typography>
              </Grid>
              <Grid item className={classes.header}>
                <a
                  rel="noopener noreferrer"
                  href={sanitizeLink(window.location.href)}
                  target="_blank"
                  id="ab"
                >
                  View Online
                </a>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} className={classes.divider}>
          <Divider top={0} width="100%" />
        </Grid>
      </Grid>
    </>
  );
}

Header.propTypes = {
  id: PropTypes.string,
  image: PropTypes.string,
  isFullWidth: PropTypes.bool,
  title: PropTypes.string,
  answers: PropTypes.object,
  actionsNode: PropTypes.node,
};

export default Header;
